import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import MovieCard from '../../components/MovieCard/MovieCard';
import './TVSeriesPage.css';

const TVSeriesPage = () => {
  // Mock TV series data (you can expand your database with actual TV series)
  const [tvSeries] = useState([
    {
      id: 101,
      title: "Stranger Things",
      year: "2016-2024",
      genre: "Sci-Fi/Horror",
      seasons: 5,
      episodes: 42,
      rating: 8.7,
      votes: "45,678",
      description: "When a young boy disappears, his mother, a police chief and his friends must confront terrifying supernatural forces in order to get him back.",
      poster: "https://example.com/stranger-things.jpg",
      categories: ["Drama", "Fantasy", "Horror"],
      status: "Ongoing",
      network: "Netflix"
    },
    {
      id: 102,
      title: "Breaking Bad",
      year: "2008-2013",
      genre: "Crime/Drama",
      seasons: 5,
      episodes: 62,
      rating: 9.5,
      votes: "89,234",
      description: "A high school chemistry teacher diagnosed with inoperable lung cancer turns to manufacturing and selling methamphetamine.",
      poster: "https://example.com/breaking-bad.jpg",
      categories: ["Crime", "Drama", "Thriller"],
      status: "Ended",
      network: "AMC"
    },
    {
      id: 103,
      title: "The Crown",
      year: "2016-2023",
      genre: "Drama/History",
      seasons: 6,
      episodes: 60,
      rating: 8.6,
      votes: "34,567",
      description: "Follows the political rivalries and romance of Queen Elizabeth II's reign and the events that shaped the second half of the twentieth century.",
      poster: "https://example.com/the-crown.jpg",
      categories: ["Biography", "Drama", "History"],
      status: "Ended",
      network: "Netflix"
    },
    {
      id: 104,
      title: "The Mandalorian",
      year: "2019-present",
      genre: "Sci-Fi/Adventure",
      seasons: 3,
      episodes: 24,
      rating: 8.7,
      votes: "56,789",
      description: "The travels of a lone bounty hunter in the outer reaches of the galaxy, far from the authority of the New Republic.",
      poster: "https://example.com/mandalorian.jpg",
      categories: ["Action", "Adventure", "Sci-Fi"],
      status: "Ongoing",
      network: "Disney+"
    },
    {
      id: 105,
      title: "Game of Thrones",
      year: "2011-2019",
      genre: "Fantasy/Drama",
      seasons: 8,
      episodes: 73,
      rating: 9.2,
      votes: "123,456",
      description: "Nine noble families fight for control over the lands of Westeros, while an ancient enemy returns after being dormant for millennia.",
      poster: "https://example.com/got.jpg",
      categories: ["Action", "Adventure", "Drama", "Fantasy"],
      status: "Ended",
      network: "HBO"
    },
    {
      id: 106,
      title: "The Last of Us",
      year: "2023-present",
      genre: "Drama/Horror",
      seasons: 1,
      episodes: 9,
      rating: 8.8,
      votes: "67,890",
      description: "Joel and Ellie, a pair connected through the harshness of the world they live in, are forced to endure brutal circumstances and ruthless killers.",
      poster: "https://example.com/last-of-us.jpg",
      categories: ["Action", "Adventure", "Drama", "Horror"],
      status: "Ongoing",
      network: "HBO"
    }
  ]);

  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('popularity');

  const filteredSeries = tvSeries.filter(series => {
    if (filterStatus === 'all') return true;
    return series.status.toLowerCase() === filterStatus.toLowerCase();
  }).sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return b.rating - a.rating;
      case 'newest':
        return parseInt(b.year.split('-')[0]) - parseInt(a.year.split('-')[0]);
      case 'title':
        return a.title.localeCompare(b.title);
      default:
        return parseInt(b.votes.replace(/,/g, '')) - parseInt(a.votes.replace(/,/g, ''));
    }
  });

  return (
    <div className="tv-series-page">
      {/* Hero Section */}
      <div className="tv-series-page__hero" data-aos="fade-down">
        <div className="tv-series-page__hero-content">
          <h1 className="tv-series-page__hero-title">TV Series</h1>
          <p className="tv-series-page__hero-subtitle">
            Binge-worthy shows and series for endless entertainment
          </p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="tv-series-page__stats">
        <div className="tv-series-page__stat" data-aos="fade-up" data-aos-delay="100">
          <span className="tv-series-page__stat-number">{tvSeries.length}</span>
          <span className="tv-series-page__stat-label">Total Series</span>
        </div>
        <div className="tv-series-page__stat" data-aos="fade-up" data-aos-delay="200">
          <span className="tv-series-page__stat-number">
            {tvSeries.filter(s => s.status === 'Ongoing').length}
          </span>
          <span className="tv-series-page__stat-label">Ongoing</span>
        </div>
        <div className="tv-series-page__stat" data-aos="fade-up" data-aos-delay="300">
          <span className="tv-series-page__stat-number">
            {tvSeries.filter(s => s.status === 'Ended').length}
          </span>
          <span className="tv-series-page__stat-label">Completed</span>
        </div>
        <div className="tv-series-page__stat" data-aos="fade-up" data-aos-delay="400">
          <span className="tv-series-page__stat-number">4</span>
          <span className="tv-series-page__stat-label">Networks</span>
        </div>
      </div>

      {/* Filters */}
      <div className="tv-series-page__filters">
        <div className="tv-series-page__filter-group">
          <label className="tv-series-page__filter-label">Status</label>
          <select
            className="tv-series-page__filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Series</option>
            <option value="ongoing">Ongoing</option>
            <option value="ended">Completed</option>
          </select>
        </div>
        
        <div className="tv-series-page__filter-group">
          <label className="tv-series-page__filter-label">Sort By</label>
          <select
            className="tv-series-page__filter-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="popularity">Most Popular</option>
            <option value="rating">Highest Rated</option>
            <option value="newest">Newest First</option>
            <option value="title">Alphabetical</option>
          </select>
        </div>
      </div>

      {/* Series Grid */}
      <div className="tv-series-page__content">
        <div className="tv-series-page__grid">
          {filteredSeries.map((series, index) => (
            <motion.article
              key={series.id}
              className="tv-series-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              whileHover={{ y: -5 }}
            >
              <div className="tv-series-card__poster">
                <img 
                  src={series.poster} 
                  alt={series.title}
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/300x450/1a1a1a/666?text=${encodeURIComponent(series.title)}`;
                  }}
                />
                {series.status === 'Ongoing' && (
                  <span className="tv-series-card__badge">ONGOING</span>
                )}
                <div className="tv-series-card__overlay">
                  <button className="tv-series-card__play">
                    <span className="material-icons">play_arrow</span>
                  </button>
                </div>
              </div>
              
              <div className="tv-series-card__info">
                <h3 className="tv-series-card__title">{series.title}</h3>
                <div className="tv-series-card__meta">
                  <span className="tv-series-card__year">{series.year}</span>
                  <span className="tv-series-card__dot">•</span>
                  <span className="tv-series-card__network">{series.network}</span>
                </div>
                <div className="tv-series-card__stats">
                  <span className="tv-series-card__seasons">{series.seasons} Seasons</span>
                  <span className="tv-series-card__episodes">{series.episodes} Episodes</span>
                </div>
                <div className="tv-series-card__rating">
                  <span className="material-icons">star</span>
                  <span>{series.rating}</span>
                </div>
                <p className="tv-series-card__description">{series.description}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TVSeriesPage;